/* eslint-disable no-param-reassign */
import _ from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { locationTargetsOptionKeys } from '@bingads-webui-campaign-react/wizard-shared-components';
import {
  getBundleCountriesAndBundleLocationCriterions,
  convertLocationStateToLocationSchemaCampaign,
  shouldSelectBundleOption,
  getLocationIntent,
  parseLocationTargets,
} from '@bingads-webui-campaign/hotel-common-targets';
import * as languageBundle from '@bingads-webui-campaign-legacy/location-targets/util/languageBundle';
import { minuteMap } from '@bingads-webui-campaign/ad-schedule';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { getTargetCriterion, hydrateCriterionWithBidAndId } from './utils';

const campaignCriterionTypeName = 'Model.CampaignCriterion';

export const LOCATION_CRITERION_TYPE = {
  LOCATION: 'Location',
  RADIUS: 'Radius',
};

export const readCampaignCriterions = (cid, aid, campaignId, type) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .CampaignCriterions
    .$cast(campaignCriterionTypeName)
    .get({
      filter: {
        where: type,
      },
    })
    .toPromise();

export const readLocationCriterions = (cid, aid, campaignId) =>
  readCampaignCriterions(
    cid, aid, campaignId,
    "isof(Criterion,'Model.LocationCriterion') or isof(Criterion,'Model.RadiusCriterion') or isof(Criterion,'Model.LocationIntentCriterion')"
  );

export const loadBundleCountries = ({
  cid,
  aid,
  serverContext,
  language: selectedLanguages,
}) => {
  const lcid = serverContext ? serverContext.CurrentUser.LocaleID : '1033';
  const market = serverContext ? serverContext.CurrentUser.Market : 'US';
  const language = selectedLanguages || 'All';

  return getBundleCountriesAndBundleLocationCriterions(cid, aid, lcid, language, market);
};

export const isBundledCountriesFound = ({
  languages,
  market,
}) => {
  const bundleCountries = languageBundle.getBundle(languages, market);
  return bundleCountries && bundleCountries.length > 0;
};

export const viewModelToBulkModelForLocationTargets = (campaignViewModel, bundleLocationCriterions) => {
  const bulkUpsertModel = convertLocationStateToLocationSchemaCampaign(
    campaignViewModel.locationTargets,
    bundleLocationCriterions,
    {
      ...campaignViewModel,
      bidStrategy: {
        option: '',
      },
      name: {
        value: campaignViewModel.Name,
      },
    }
  );
  return bulkUpsertModel;
};

export const bulkModelToViewModelForLocationTargets = ({
  locationTargetsModel, bundleCountries,
}) => {
  const locations = locationTargetsModel
    .filter(criterion => criterion.Criterion.Type === LOCATION_CRITERION_TYPE.RADIUS || criterion.Criterion.Type === LOCATION_CRITERION_TYPE.LOCATION);
  const intent = locationTargetsModel.find(criterion => criterion.Criterion.Type === 'LocationIntentCriterion');
  const intentId = intent ? intent.Id : null;
  const intentOption = intent ? getLocationIntent(intent.Criterion.LocationIntentCriterion.IntentOption) : null;
  let targetOption = locations.length ? locationTargetsOptionKeys.specific : locationTargetsOptionKeys.everywhere;
  targetOption = shouldSelectBundleOption(bundleCountries, locations) ? locationTargetsOptionKeys.bundleOrDefaultMarket : targetOption;
  return {
    locationTargets: {
      currentLevelSettings: {
        initLocations: locations,
        currentLocations: locations,
        targetOption,
        intentId,
        ...intentOption,
      },
      bidMode: false,
      useParentLevel: locations.length === 0,
    },
  };
};

export const modelToViewModelForLocationTargets = ({
  model, bundleCountries, campaignId,
}) => {
  const {
    locations,
    intentId,
    intentOption,
  } = parseLocationTargets(model, 'campaign', campaignId);
  let targetOption = locations.length ? locationTargetsOptionKeys.specific : locationTargetsOptionKeys.everywhere;
  targetOption = shouldSelectBundleOption(bundleCountries, locations) ? locationTargetsOptionKeys.bundleOrDefaultMarket : targetOption;
  return {
    locationTargets: {
      currentLevelSettings: {
        initLocations: locations,
        currentLocations: locations,
        targetOption,
        intentId,
        ...intentOption,
      },
      bidMode: false,
      useParentLevel: locations.length === 0,
    },
  };
};

export const readScheduleCriterions = (cid, aid, campaignId) =>
  readCampaignCriterions(
    cid, aid, campaignId,
    "isof(Criterion,'Model.DayTimeCriterion')"
  );

export const viewModelToBulkModelForSchedule = (adSchedulesBulkUpsert, campaignModel) => {
  _.each(adSchedulesBulkUpsert, (criterion) => {
    criterion.Status = _.isEmpty(criterion.Status) ? 'Active' : criterion.Status;
    criterion.Campaign = {
      Id: campaignModel.Id,
    };
  });
  return adSchedulesBulkUpsert;
};

export const modelToViewModelForDayTimeTargets = (criterions) => {
  const schedulingRows = [];

  _.chain(criterions).each((criterion) => {
    schedulingRows.push({
      Id: Number(criterion.Id),
      Day: criterion.Criterion.Day,
      StartHour: criterion.Criterion.FromHour,
      StartMinute: criterion.Criterion.FromMinute,
      EndHour: criterion.Criterion.ToHour,
      EndMinute: criterion.Criterion.ToMinute,
      BidAdjustmentType: criterion.CriterionBid.Multiplier < 0 ? -1 : 1,
      BidAdjustmentAmount: window.Math.abs(criterion.CriterionBid.Multiplier),
    });
  })
    .value();

  return schedulingRows;
};

export const bulkModelToViewModelForDayTimeTargets = (criterions, originalCriterions) => {
  const deletedIds = _.chain(criterions).where({
    CriterionType: 'DayTime',
    Status: 'Deleted',
  }).map(criterion => criterion.Id).value();

  let added = _.chain(criterions)
    .filter(criterion => criterion.CriterionType === 'DayTime' && criterion.Status !== 'Deleted')
    .map(criterion => ({
      Id: Number(criterion.Id),
      Day: criterion.Criterion.DayTimeCriterion.Day,
      StartHour: criterion.Criterion.DayTimeCriterion.FromHour,
      StartMinute: minuteMap[criterion.Criterion.DayTimeCriterion.FromMinute],
      EndHour: criterion.Criterion.DayTimeCriterion.ToHour,
      EndMinute: minuteMap[criterion.Criterion.DayTimeCriterion.ToMinute],
      BidAdjustmentType: criterion.Criterion.BidAdjustment < 0 ? -1 : 1,
      BidAdjustmentAmount: window.Math.abs(criterion.Criterion.BidAdjustment),
    }))
    .value();

  added = _.filter(added, range => !_.find(originalCriterions, r => _.isMatch(r, range)));

  const dayTimeRanges = _.filter(originalCriterions.concat(added), range => _.indexOf(deletedIds, range.Id.toString()) === -1);

  return dayTimeRanges;
};

export const readTargetCriterion = (cid, aid, campaignId) =>
  readCampaignCriterions(
    cid, aid, campaignId,
    "isof(Criterion,'Model.AgeCriterion') or isof(Criterion,'Model.GenderCriterion') or isof(Criterion,'Model.DeviceCriterion') or isof(Criterion,'Model.CompanyNameCriterion') or isof(Criterion,'Model.IndustryCriterion') or isof(Criterion,'Model.JobFunctionCriterion')"
  );

export const deviceName = ['Tablets', 'Computers', 'Smartphones'];

export const compareDevice = (Targets, initialTargets) => {
  if (Targets.length !== deviceName.length) {
    return false;
  }
  let noChanged = true;
  deviceName.forEach((name) => {
    const currentDevice = Targets.filter(target => target.Criterion.DeviceCriterion.DeviceName === name);
    const initialDevice = initialTargets.filter(target => target.Criterion.DeviceCriterion.DeviceName === name);
    if (!currentDevice || !initialDevice || currentDevice.length !== 1 || initialDevice.length !== 1 || currentDevice[0].Criterion.BidAdjustment !== initialDevice[0].Criterion.BidAdjustment) {
      noChanged = false;
    }
  });
  return noChanged;
};

export const viewModelToBulkModelForDeviceTargets = ({ campaignViewModel }) => {
  const deviceTargets = campaignViewModel.Device || [];
  const initialDevice = campaignViewModel.initialDevice || [];
  if (compareDevice(deviceTargets, initialDevice)) {
    return [];
  }
  const addedDevices = [];
  // eslint-disable-next-line array-callback-return
  deviceTargets.map((target) => {
    addedDevices.push({
      Criterion: _.extend(target.Status === 'Deleted' ? { BidAdjustment: 0 } : {}, target.Criterion),
      Id: target.Id,
      Status: target.Status || 'Active',
      CriterionType: target.Criterion.Type,
      Campaign: {
        Id: campaignViewModel.Id,
      },
    });
  });
  return addedDevices;
};

export const modelToViewModelForDeviceTargets = ({ model, campaignId }) => {
  let deviceTargets = [];
  let initialDevice = [];
  if (model && !_.isEmpty(model)) {
    deviceTargets = model.map(target => ({
      Id: target.Id,
      Status: target.Status || 'Active',
      Criterion: _.extend(target.Criterion, { BidAdjustment: target.CriterionBid.Multiplier, DeviceCriterion: { DeviceName: target.Criterion.DeviceName } }),
      CriterionType: 'Device',
      Campaign: {
        Id: campaignId,
      },
    }));
  } else {
    deviceTargets = deviceName.map(name => ({
      Status: 'Active',
      Criterion: {
        '@odata.type': '#Model.DeviceCriterion',
        Type: 'Device',
        Name: null,
        OSName: null,
        DeviceCriterion: { DeviceName: name },
        BidAdjustment: 0,
      },
      CriterionType: 'Device',
      Campaign: {
        Id: campaignId,
      },
    }));
  }
  initialDevice = cloneDeep(deviceTargets);
  return {
    Device: deviceTargets,
    initialDevice,
  };
};

export const GenderName = ['Male', 'Female'];

export const modelToViewModelForGenderTargets = ({ model, campaignId }) => {
  let genderTargets = [];
  let initialGender = [];
  if (model && !_.isEmpty(model)) {
    genderTargets = model.map(target => ({
      Id: target.Id,
      Status: target.Status || 'Active',
      Criterion: _.extend(target.Criterion, { BidAdjustment: target.CriterionBid.Multiplier, GenderCriterion: { Target: target.Criterion.GenderType } }),
      CriterionType: 'Gender',
      Campaign: {
        Id: campaignId,
      },
    }));
  } else {
    genderTargets = GenderName.map(name => ({
      Status: 'Active',
      Criterion: {
        '@odata.type': '#Model.GenderCriterion',
        Type: 'Gender',
        Name: null,
        OSName: null,
        GenderCriterion: { Target: name },
        BidAdjustment: 0,
      },
      CriterionType: 'Gender',
      Campaign: {
        Id: campaignId,
      },
    }));
  }
  initialGender = cloneDeep(genderTargets);
  return {
    Gender: genderTargets,
    initialGender,
  };
};

const compareGender = (Targets, initialTargets) => {
  let changed = false;
  GenderName.forEach((name) => {
    const currentTarget = Targets.find(target => target.Criterion.GenderCriterion.Target === name);
    const initialTarget = initialTargets.find(target => target.Criterion.GenderCriterion.Target === name);
    if (initialTarget && currentTarget && currentTarget.Criterion.BidAdjustment !== initialTarget.Criterion.BidAdjustment) {
      changed = true;
    }
  });
  return changed;
};

export const viewModelToBulkModelForGenderTargets = ({ campaignViewModel }) => {
  const genderTargets = campaignViewModel.Gender || [];
  const initialGender = campaignViewModel.initialGender || [];
  const addedGender = [];
  if (compareGender(genderTargets, initialGender)) {
    genderTargets.forEach((target) => {
      addedGender.push({
        Criterion: target.Criterion,
        Id: target.Id,
        Status: target.Status || 'Active',
        CriterionType: target.Criterion.Type,
        Campaign: {
          Id: campaignViewModel.Id,
        },
      });
    });
  }
  return addedGender;
};

export const AgeName = ['EighteenToTwentyFive', 'TwentyFiveToThirtyFive', 'ThirtyFiveToFifty', 'FiftyToSixtyFive', 'SixtyFiveAndAbove'];

export const modelToViewModelForAgeTargets = ({ model, campaignId }) => {
  let initialAge = [];
  const ageTargets = AgeName.map((name) => {
    const targetItem = model && model.find(target => target.Criterion.AgeRange === name);
    if (targetItem) {
      return {
        Id: targetItem.Id,
        Status: targetItem.Status || 'Active',
        Criterion: _.extend(targetItem.Criterion, { BidAdjustment: targetItem.CriterionBid.Multiplier, AgeCriterion: { Target: targetItem.Criterion.AgeRange } }),
        CriterionType: 'Age',
        Campaign: {
          Id: campaignId,
        },
      };
    }
    return {
      Status: 'Active',
      Criterion: {
        '@odata.type': '#Model.AgeCriterion',
        Type: 'Age',
        AgeCriterion: { Target: name },
        BidAdjustment: 0,
      },
      CriterionType: 'Age',
      Campaign: {
        Id: campaignId,
      },
    };
  });

  initialAge = cloneDeep(ageTargets);
  return {
    Age: ageTargets,
    initialAge,
  };
};

export const viewModelToBulkModelForAgeTargets = ({ campaignViewModel }) => {
  const ageTargets = campaignViewModel.Age || [];
  const initialAge = campaignViewModel.initialAge || [];
  const addedAge = [];
  ageTargets.forEach((target) => {
    const initialTarget = initialAge.find(age => age.Criterion.AgeCriterion.Target === target.Criterion.AgeCriterion.Target);
    if (initialTarget && target.Criterion.BidAdjustment !== initialTarget.Criterion.BidAdjustment) {
      addedAge.push({
        Criterion: target.Criterion,
        Id: target.Id,
        Status: target.Status || 'Active',
        CriterionType: target.Criterion.Type,
        Campaign: {
          Id: campaignViewModel.Id,
        },
      });
    }
  });

  return addedAge;
};

export const modelToViewModelForCompanyNameTargets = ({ model }) => {
  let companyNameTargets = [];
  let initialCompanyNameTargets = [];
  if (model && !_.isEmpty(model)) {
    companyNameTargets = model.map(target => ({
      Id: target.Id,
      Target: target.Criterion.ProfileId,
      Size: target.Criterion.Size,
      Industry: target.Criterion.Industry,
      Name: target.Criterion.CompanyName,
      BidAdjustment: target.CriterionBid.Multiplier,
    }));
  }
  initialCompanyNameTargets = cloneDeep(companyNameTargets);
  return {
    companyNameTargets,
    initialCompanyNameTargets,
  };
};

export const viewModelToBulkModelForCompanyNameTargets = ({ campaignViewModel }) => {
  const companyNameTargets = campaignViewModel.companyNameTargets || [];
  const initialCompanyNameTargets = campaignViewModel.initialCompanyNameTargets || [];

  const addedCompanyNameTargets = companyNameTargets.filter(target => initialCompanyNameTargets.find(initialTarget =>
    initialTarget.Target === target.Target && initialTarget.BidAdjustment === target.BidAdjustment) === undefined);
  const removedCompanyNameTargets = initialCompanyNameTargets.filter(initialTarget => companyNameTargets.find(target =>
    initialTarget.Target === target.Target) === undefined).map(target => ({ ...target, criterion: target.Target, status: 'Deleted' }));

  const addedCompanyNameCriterion = getTargetCriterion({
    targets: addedCompanyNameTargets.map(({ Id, ...criterion }) => ({ Id, criterion })), type: 'CompanyName', propKey: 'Target', campaignViewModel,
  });
  const removedCompanyNameCriterion = getTargetCriterion({
    targets: removedCompanyNameTargets, type: 'CompanyName', propKey: 'Target', campaignViewModel,
  });

  return [
    ...addedCompanyNameCriterion,
    ...removedCompanyNameCriterion,
  ];
};

export const modelToViewModelForIndustryTargets = ({ model }) => {
  let industryTargets = [];
  let initialIndustry = [];
  if (model && !_.isEmpty(model)) {
    industryTargets = model.map(target => ({
      Id: target.Id,
      BidAdjustment: target.CriterionBid.Multiplier,
      criterion: target.Criterion.ProfileId,
    }));
  }
  initialIndustry = cloneDeep(industryTargets);
  return {
    industry: industryTargets.map(target => target.criterion),
    initialIndustry,
    initialIndustryTargets: initialIndustry,
  };
};

export const viewModelToBulkModelForIndustryTargets = ({ campaignViewModel }) => {
  const industryTargets = campaignViewModel.industry || [];
  const initialIndustryTargets = campaignViewModel.initialIndustry || [];

  const { industryTargetsBidAdjustment } = campaignViewModel;
  const isIndustryTargetBidAdjustmentUpdated = ({ criterion, BidAdjustment }) => industryTargetsBidAdjustment.find(targetWithBid =>
    _.get(targetWithBid, 'Criterion.IndustryCriterion.Target') === criterion && _.get(targetWithBid, 'Criterion.BidAdjustment') === BidAdjustment);

  const addedIndustryTargets = industryTargets.filter(target => initialIndustryTargets.find(initialTarget =>
    initialTarget.criterion === target && (!industryTargetsBidAdjustment || isIndustryTargetBidAdjustmentUpdated(initialTarget))) === undefined);
  const removedIndustryTargets = initialIndustryTargets.filter(initialTarget => industryTargets.find(target =>
    initialTarget.criterion === target) === undefined).map(target => ({ ...target, status: 'Deleted' }));

  const addedIndustryCriterion = getTargetCriterion({
    targets: addedIndustryTargets.map(criterion => ({ criterion })), type: 'Industry', propKey: 'Target', campaignViewModel,
  });
  const removedIndustryCriterion = getTargetCriterion({
    targets: removedIndustryTargets, type: 'Industry', propKey: 'Target', campaignViewModel,
  });

  if (industryTargetsBidAdjustment && industryTargetsBidAdjustment.length > 0) {
    hydrateCriterionWithBidAndId('Industry', initialIndustryTargets, addedIndustryCriterion, industryTargetsBidAdjustment);
  }

  return [
    ...addedIndustryCriterion,
    ...removedIndustryCriterion,
  ];
};

export const modelToViewModelForJobFunctionTargets = ({ model }) => {
  let jobFunctionTargets = [];
  let initialJobTargets = [];
  if (model && !_.isEmpty(model)) {
    jobFunctionTargets = model.map(target => ({
      Id: target.Id,
      BidAdjustment: target.CriterionBid.Multiplier,
      criterion: target.Criterion.ProfileId,
    }));
  }
  initialJobTargets = cloneDeep(jobFunctionTargets);
  return {
    jobTargets: jobFunctionTargets.map(target => target.criterion),
    initialJobTargets,
    initialJobFunctionTargets: initialJobTargets,
  };
};

export const viewModelToBulkModelForJobFunctionTargets = ({ campaignViewModel }) => {
  const jobTargets = campaignViewModel.jobTargets || [];
  const initialJobTargets = campaignViewModel.initialJobTargets || [];

  const { jobTargetBidAdjustments } = campaignViewModel;
  const isJobTargetBidAdjustmentUpdated = ({ criterion, BidAdjustment }) => jobTargetBidAdjustments.find(targetWithBid =>
    _.get(targetWithBid, 'Criterion.JobFunctionCriterion.Target') === criterion && _.get(targetWithBid, 'Criterion.BidAdjustment') === BidAdjustment);

  const addedJobTargets = jobTargets.filter(target => initialJobTargets.find(initialTarget =>
    initialTarget.criterion === target && (!jobTargetBidAdjustments || isJobTargetBidAdjustmentUpdated(initialTarget))) === undefined);
  const removedJobTargets = initialJobTargets.filter(initialTarget => jobTargets.find(target =>
    initialTarget.criterion === target) === undefined).map(target => ({ ...target, status: 'Deleted' }));

  const addedJobCriterion = getTargetCriterion({
    targets: addedJobTargets.map(criterion => ({ criterion })), type: 'JobFunction', propKey: 'Target', campaignViewModel,
  });
  const removedJobCriterion = getTargetCriterion({
    targets: removedJobTargets, type: 'JobFunction', propKey: 'Target', campaignViewModel,
  });

  if (jobTargetBidAdjustments && jobTargetBidAdjustments.length > 0) {
    hydrateCriterionWithBidAndId('JobFunction', initialJobTargets, addedJobCriterion, jobTargetBidAdjustments);
  }

  return [
    ...addedJobCriterion,
    ...removedJobCriterion,
  ];
};
